import 'react-simple-toasts/dist/style.css'
import 'react-simple-toasts/dist/theme/plain.css'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/shift-away.css'
import 'tippy.js/themes/light-border.css'

/* global HeadersInit, RequestInit */
import { type SortingState } from '@tanstack/react-table'
import { type ClassValue, clsx } from 'clsx'
import {
  isPossiblePhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js'
import type React from 'react'
import { type UseFormReturn } from 'react-hook-form'
import { type ToastOptions } from 'react-simple-toasts'
import { twMerge } from 'tailwind-merge'
import tippy from 'tippy.js'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { type GrapeMoney, type MoneyAmount, type MoneyValue } from 'types'
import { type Money } from 'utils/web_api.schemas'

import { type EnrollmentCategory } from './components/brokers/types'

export function cn (...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const capitalize = (str: string) => {
  return str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : str
}

export const getNumericValueFromNumberOrString = (
  value: string | number
): number => {
  return typeof value === 'number'
    ? value
    : parseFloat(value.replace(/[^\d.]/g, ''))
}

/**
 * Fetch with default JSON and Rails headers.
 *
 * This is a very lite wrapper around fetch() and intended to only be
 * temporarily used for internal JSON API calls until we move calls over to
 * Orval-generated methods. It adds default headers that work well with Rails,
 * helping it understand that it's an XHR and "non-navigational format", which
 * prevents Rails from inadvertently redirecting users to the URL after logging
 * in.
 *
 * It shouldn't really be used for new calls, which should prefer the generated
 * clients and methods instead, so this is added as a deprecated shim to
 * discourage its use.
 *
 * @deprecated use Orval-generated clients
 * @param resource
 * @param options
 * @returns awaitable response object
 */
export const fetchWithHeaders = async (
  resource: string,
  options: RequestInit = {}
) => {
  return await fetch(resource, {
    ...options,
    headers: getHeaders(options.headers),
  })
}

export const formatList = (strs: Array<string>) => {
  if (!strs.length) return ''
  if (strs.length === 1) return strs[0]
  if (strs.length === 2) return `${strs[0]} and ${strs[1]}`
  else {
    const lastItem = strs.pop()
    return `${strs.join(', ')}, and ${lastItem}`
  }
}

export const isValidDateInput = (dateString: string): boolean => {
  // Regular expression to match the YYYY-MM-DD format
  const regex = /^\d{4}-\d{2}-\d{2}$/
  if (!regex.test(dateString)) return false

  const date = new Date(dateString)
  const year = date.getFullYear()

  // Check if the year is within a realistic range
  if (year < 1900 || year > 2099) return false
  return true
}

export const formatDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const beginningOfMonth = (month: number, year: number) => {
  return new Date(year, month, 1)
}

export const endOfMonth = (month: number, year: number) => {
  return new Date(year, month + 1, 0)
}

export function getLocaleDateString (date: Date) {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  })
}

function parseDateOnly (dateStr: string): Date {
  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/
  if (dateOnlyPattern.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day) // 0-indexed month
  }
  return new Date(dateStr)
}

export function getFormattedDate (
  dateStr: string,
  options: Intl.DateTimeFormatOptions = {}
) {
  const date = parseDateOnly(dateStr)

  const baseOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }

  return new Intl.DateTimeFormat('en-US', {
    ...baseOptions,
    ...options,
  }).format(date)
}

export function formatMoney (value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value)
}

export function getTimezoneDatetimeStrings ({
  date,
  timeZone,
  includeSeconds,
}: {
  date: Date;
  timeZone?: string;
  includeSeconds?: boolean;
}) {
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone,
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
    timeZone,
  }

  const formattedDate = date.toLocaleDateString('en-US', dateOptions)
  const formattedTime = date.toLocaleTimeString('en-US', timeOptions)

  return {
    formattedDate,
    formattedTime,
  }
}

export function getTimezoneDatetimeString (date: Date, timeZone: string) {
  const { formattedDate, formattedTime } = getTimezoneDatetimeStrings({
    date,
    timeZone,
    includeSeconds: true,
  })
  return `${formattedDate}, ${formattedTime} ${timeZone}`
}

export function getUTCDatetimeString (date: Date) {
  return getTimezoneDatetimeString(date, 'UTC')
}

export function getBrowserTimezoneDatetimeString (date: Date) {
  const formattedString = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  })
  return `${formattedString}`
}

/* Converts the date into YYYY-MM-DD format */
export function getISODateString (date: Date) {
  return date.toISOString().split('T')[0]
}

export function getLocaleCurrencyString (number: number) {
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
}

export function getMinifiedLocaleCurrencyStringFromMoneyAmount (
  amount: MoneyAmount | undefined | null,
  options?: Intl.NumberFormatOptions
) {
  if (!amount) return ''

  const numericValue = parseFloat(amount.value)
  const hasNoCents = numericValue % 1 === 0

  const formattingOptions: Intl.NumberFormatOptions = {
    ...options,
  }

  if (hasNoCents) {
    // If it's a whole number, hide the cents
    formattingOptions.minimumFractionDigits = 0
    formattingOptions.maximumFractionDigits = 0
  }

  return getLocaleCurrencyStringFromString(amount.value, formattingOptions)
}

export function getLocaleCurrencyStringFromMoneyAmount (
  amount: MoneyAmount | undefined,
  options?: Intl.NumberFormatOptions
) {
  if (!amount) return 'NA'
  return getLocaleCurrencyStringFromString(amount.value, options)
}

export function getLocaleCurrencyStringFromMoneyValue (
  amount: MoneyValue | undefined,
  options?: Intl.NumberFormatOptions
) {
  if (!amount) return 'NA'
  return getLocaleCurrencyStringFromNumber(amount.amount / 100, options)
}

export function getLocaleCurrencyStringFromNumber (
  amount: number,
  options?: Intl.NumberFormatOptions
) {
  let isNegative = false

  if (amount < 0) {
    isNegative = true
    amount = Math.abs(amount)
  }

  const formattedNumber = amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    ...options,
  })

  return isNegative ? `(${formattedNumber})` : formattedNumber
}

export function getLocaleCurrencyStringFromString (
  amount: string,
  options?: Intl.NumberFormatOptions
) {
  return getLocaleCurrencyStringFromNumber(parseFloat(amount), options)
}

export const getCSRFToken = () => {
  const meta = document.querySelector('meta[name=csrf-token]')
  const token = meta && meta.getAttribute('content')
  return token
}

export const getHeaders = (
  headers?: HeadersInit,
  options?: { skipContentType?: boolean }
): HeadersInit => {
  const baseHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }

  // Only set Content-Type if not explicitly skipped (e.g., for FormData uploads)
  if (!options?.skipContentType) {
    baseHeaders['Content-Type'] = 'application/json'
  }

  return {
    ...baseHeaders,
    // Keep this last so that callers can override default headers.
    ...headers,
  }
}

// handleTanstackSortingStateForSingleColumn handles single column sorting and loops through a
// few sorting states: Asc --> Desc --> Default
export function handleTanstackSortingStateForSingleColumn (
  newId: string,
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>,
  defaultSorting: SortingState = []
) {
  setSorting((prevState) => {
    // If no sorting state is present, add the selected column ID to sorting state.
    if (!prevState?.length) {
      return [{ id: newId, desc: false }, ...defaultSorting]
    }
    const [{ id, desc }] = prevState

    // If a sorting state exists, but the ID selected is not the same as the ID we're currently sorting by
    // a new column has been selected. Overwrite the sorting state with the new ID.
    if (id !== newId) return [{ id: newId, desc: false }, ...defaultSorting]

    // If the ID of the existing state and the new state are equal, toggle the existing state from ascending to descending.
    if (!desc) return [{ id: newId, desc: true }, ...defaultSorting]

    // If the ID selected is already showing a descending sorting state, go back to the default state.
    return defaultSorting
  })
}

export function handleStatusHover (event: any, content: string): void {
  event.stopPropagation()
  tippy(event.target, {
    content,
    theme: 'light-border',
    animation: 'shift-away',
    arrow: true,
    placement: 'bottom',
    trigger: 'mouseenter',
    delay: 500,
  })
}

export function createHashMapByKey<T, K extends keyof T> (
  data: T[],
  key: K
): { [key: string]: T } {
  return data.reduce(
    (acc, curr) => ({ ...acc, [curr[key] as string]: curr }),
    {}
  )
}

// TODO(tamara): add tests!
export const convertNillableGrapeMoneyToNumber = (
  money: GrapeMoney | null | undefined
): number => {
  if (!money) {
    return 0
  }

  return money.subunits / 100
}

export function sentenceCase (str: string) {
  const words = str.split('_')
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1)
  return words.join(' ')
}

interface HasCreatedAt {
  created_at: string;
}

export function sortByCreatedAt<T extends HasCreatedAt> (a: T, b: T) {
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
}

export const toastOptions: ToastOptions = {
  position: 'top-center',
  duration: 3000,
  theme: 'plain',
  className:
    'ml-64 border border-green-500 bg-green-100 text-green-800 p-4 rounded-lg',
}

export const toasttOptionsError: ToastOptions = {
  className:
    'ml-64 border border-red-500 bg-red-100 text-red-800 p-4 rounded-lg',
}

// localization, what's that
export function getEnglishLanguageDateFromDateString (dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function getEnglishLanguageMonthYearFromDateString (
  dateStr: string
): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  })
}

export function getYearFromDateString (dateStr: string): number {
  return new Date(dateStr + 'T00:00:00').getFullYear()
}

export function getLocalizedDateString (
  dateToLocalize: Date,
  includeYear: boolean = true
): string {
  return dateToLocalize.toLocaleDateString('en-US', {
    year: includeYear ? 'numeric' : undefined,
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export function conditionallyLocalizeDate (dateToLocalize: Date): string {
  // if the date being rendered takes place this current year, do not include year
  // otherwise, include the year

  const today = new Date()
  return getLocalizedDateString(
    dateToLocalize,
    dateToLocalize.getFullYear() !== today.getFullYear()
  )
}

export function getDateString (date: Date): string {
  return date.toISOString().split('T')[0]
}

export function getDateStringNoTimezone (date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function getPSTDate (date: Date) {
  return new Date(date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }))
}
export function getPSTToday () {
  return getPSTDate(new Date())
}

export function getUTCDate (date: Date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  )
}

export function getUTCToday () {
  const now = new Date()
  return getUTCDate(now)
}

export function convertToUTCDate (date: Date) {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000)
}

export function getDateNumDaysFromStartDate (dateStr: string, numDays: string) {
  const date = getUTCDate(new Date(dateStr))
  date.setDate(getUTCDate(new Date(dateStr)).getDate() + parseInt(numDays))

  return date
}

export function snakeToSentenceCase (str: string) {
  return str.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase())
}

export function sanitizeNumericInput (value: string) {
  return value.replace(/[^0-9.]/g, '')
}

// This method statically fails typechecking if passed a value that is not of
// type `never`. This is useful for exhaustiveness checking in switch statements.
export function assertUnreachable (_: never) {}

export const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export function getEnrollmentStartDateOptions (year: number) {
  return months.map((month, index) => ({
    value: formatDate(new Date(year, index, 1)),
    label: `${month} 01 ${year}`,
  }))
}

export function getEnrollmentEndDateOptions (year: number) {
  return months.map((month, index) => {
    const date = new Date(year, index + 1, 0)
    return {
      value: formatDate(date),
      label: `${month} ${date.getDate()} ${year}`,
    }
  })
}

export function getEnrollmentStartDateOptionsWithTooltip (
  year: number,
  employerIchraConfig?: any,
  tag?: React.ReactNode
) {
  return getEnrollmentStartDateOptions(year).map(({ value, label }) => ({
    value,
    label,
    tag:
      employerIchraConfig?.coverage_start_date &&
      new Date(employerIchraConfig?.coverage_start_date).getTime() >
        new Date(value).getTime()
        ? tag
        : null,
  }))
}

export const enrollmentCategories: Record<EnrollmentCategory, string> = {
  general_medical: 'General Medical',
  guardian_dental: ' Guardian Dental',
  renaissance_dental: 'Renaissance Dental',
  bsca_dental: 'BlueShield of California Dental',
  bsca_vision: 'BlueShield of California Vision',
  vsp: 'VSP',
  medicare: 'Medicare',
  medicare_part_a: 'Medicare (Part A)',
  medicare_part_b: 'Medicare (Part B)',
  medicare_part_c: 'Medicare (Part C)',
  medicare_part_d: 'Medicare (Part D)',
  medicare_supplement: 'Medicare Supplement',
  off_thatch_medical: 'Off-Thatch Medical',
  subsidy: 'Subsidy',
  off_thatch_dental: 'Off-Thatch Dental',
  off_thatch_vision: 'Off-Thatch Vision',
}

export function truncateText (text: string, maxLength: number = 80) {
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

const STATE_ABBREVIATIONS = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  MS: 'Mississippi',
  MT: 'Montana',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'Washington D.C.',
}

export const fullStateNameFromAbbreviation = (abbreviation: string) => {
  return STATE_ABBREVIATIONS[
    abbreviation.toUpperCase() as keyof typeof STATE_ABBREVIATIONS
  ]
}

/**
 * Calculates the age of a person at a specific date
 * @param birthDate - The date of birth (string in YYYY-MM-DD format or Date object)
 * @param targetDate - The date to calculate age at (string in YYYY-MM-DD format or Date object)
 * @returns The age at the target date
 */
export const calculateAgeAtDate = (
  birthDate: string | Date,
  targetDate: string | Date
): number => {
  // Convert to Date objects if they're strings
  const birth = typeof birthDate === 'string' ? new Date(birthDate) : birthDate
  const target =
    typeof targetDate === 'string' ? new Date(targetDate) : targetDate

  // Calculate age
  let age = target.getFullYear() - birth.getFullYear()
  const monthDiff = target.getMonth() - birth.getMonth()

  // Adjust age if birthday hasn't occurred yet in the target year
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && target.getDate() < birth.getDate())
  ) {
    age--
  }

  return age
}

export const validatePOBoxAddressLine1 = (
  value: string,
  skipValidation: boolean
) => {
  if (skipValidation) return true
  const pattern = '((([Pp][ .]*[Oo].?)?[ .]?([Bb][Oo][Xx]))|[Pp][Mm][Bb]) [0-9]+'
  if (value.match(pattern)) {
    return 'PO Boxes are not allowed. Please enter a valid street address.'
  }
  return true
}

export const validatePOBoxAddressLine2 = (
  value: string,
  skipValidation: boolean
) => {
  if (skipValidation) return true
  const pattern =
    '((([Pp][ .]*[Oo].?)?[ .]?([Bb][Oo][Xx]))|[Pp][Mm][Bb]) [0-9]+'
  if (value.match(pattern)) {
    return 'PO Boxes are not allowed. Please enter a valid street address.'
  }
  return true
}

export const numberToOrdinal = (num: number) => {
  // Special cases for numbers ending in 11, 12, or 13
  if (num % 100 >= 11 && num % 100 <= 13) {
    return `${num}th`
  }

  // Handle numbers ending in 1, 2, or 3
  switch (num % 10) {
    case 1:
      return `${num}st`
    case 2:
      return `${num}nd`
    case 3:
      return `${num}rd`
    default:
      return `${num}th`
  }
}

export const convertGrapeMoneyToNumber = (
  money: GrapeMoney | Money | null | undefined
): number => {
  if (!money) {
    return 0
  }
  return Number(money.subunits / 100)
}

export const convertCentsToGrapeMoney = (cents: number): GrapeMoney => {
  return {
    formatted_amount: formatMoney(cents / 100),
    subunits: cents,
    currency: 'USD',
  }
}

export const convertMoneyToGrapeMoney = (money: Money | null | undefined): GrapeMoney => {
  if (!money) {
    return {
      formatted_amount: '$0.00',
      subunits: 0,
      currency: 'USD' as const,
    }
  }
  return {
    formatted_amount: money.formatted_amount,
    subunits: money.subunits,
    currency: 'USD' as const,
  }
}

export const formatSSN = (
  ssn: string,
  obfuscate: boolean = false,
  obfuscationCharacter: string = 'X'
) => {
  let cleaned = ssn.replace(/\D/g, '') // Remove non-numeric characters
  if (cleaned.length > 0) {
    if (cleaned.length > 3) {
      const parts = []
      parts.push(
        obfuscate ? obfuscationCharacter.repeat(3) : cleaned.slice(0, 3)
      )
      if (cleaned.length <= 5) {
        parts.push(cleaned.slice(3))
      } else {
        parts.push(
          obfuscate ? obfuscationCharacter.repeat(2) : cleaned.slice(3, 5)
        )
        parts.push(cleaned.slice(5, 9))
      }
      cleaned = parts.join('-')
    }
  } // Format as XXX-XX-XXXX

  return cleaned
}

/**
 * Checks if a person is an adult (18 or older) at a given date
 * @param dateOfBirth - The date of birth (string in YYYY-MM-DD format)
 * @param targetDate - The date to check age at (defaults to current date)
 * @returns boolean indicating if the person is an adult
 */
export const isAdult = (
  dateOfBirth: string,
  targetDate: Date = new Date()
): boolean => {
  return calculateAgeAtDate(dateOfBirth, targetDate) >= 18
}

export const getFirstName = (
  fullName: string | null | undefined
): string | null => {
  if (!fullName) return null
  if (fullName.includes(',')) {
    return fullName.split(',')[1]?.trim()
  }
  return fullName.split(' ')[0]?.trim()
}

export const CENSUS_TEMPLATE_PATH = '/data/thatch_quoting_census_template.csv'

export function formatBoolean (val?: boolean | null): string | null | undefined {
  if (val === undefined || val === null) {
    return val
  }
  return val ? 'Yes' : 'No'
}

export const formatFileSize = (size: number | string) => {
  if (typeof size === 'string') return size
  if (size === 0) return '0 B'

  const units = ['B', 'kB', 'MB', 'GB']
  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1
  )
  const number = size / Math.pow(1024, unitIndex)
  const value = number.toLocaleString('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })

  return `${value} ${units[unitIndex]}`
}

export const formatPhoneNumber = (phoneNumber: string) => {
  let formattedPhoneNumber = ''
  try {
    formattedPhoneNumber =
      phoneNumber && isPossiblePhoneNumber(phoneNumber)
        ? parsePhoneNumberWithError(phoneNumber, 'US').formatNational()
        : phoneNumber
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_error) {
    formattedPhoneNumber = phoneNumber
  }
  return formattedPhoneNumber
}

/** Converts HTTP parameter formats like "admin[name]" to "admin.name" */
export function getPathFromField (field: string) {
  return field.replace(/\[([^\]]+)\]/g, '.$1')
}

/*
  Handles field errors from a GrapeAPIErrorType and sets them in a form. Use with forms from react-hook-form that
  have an error message displayed on the fields.
  @param error - The GrapeAPIErrorType to handle.
  @param form - The form to set the errors in.
*/
export function handleGrapeAPIFieldErrors (error: GrapeAPIErrorType, form: UseFormReturn<any>) {
  if (error.fieldErrors) {
    Object.entries(error.fieldErrors).forEach(([field, errorMessages]) => {
      const path = getPathFromField(field) as Parameters<typeof form.register>[0]

      form.setError(path, {
        type: 'manual',
        // We can't currently support rendering multiple error messages for a
        // single field, so we'll just use the first one if there are multiple.
        message: errorMessages[0],
      })
    })
  }
}

/**
 * Creates a deferred promise that can be resolved or rejected externally.
 * Useful for testing scenarios where you need precise control over when
 * a promise resolves.
 *
 * @returns A promise with resolve and reject methods attached
 */
export function deferredPromise () {
  let res!: (value: unknown) => void
  let rej!: (reason?: any) => void

  const promise = new Promise((resolve, reject) => {
    res = resolve
    rej = reject
  }) as Promise<unknown> & { resolve: typeof res; reject: typeof rej }

  promise.resolve = res
  promise.reject = rej

  return promise
}

/**
 * Formats a duration in milliseconds to a human-readable string
 * @param milliseconds - The duration in milliseconds
 * @returns A formatted string like "2h 30m", "45m 10s", or "30s"
 */
export const formatDuration = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) {
    const remainingHours = hours % 24
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
  } else if (hours > 0) {
    const remainingMinutes = minutes % 60
    return `${hours}h ${remainingMinutes}m`
  } else if (minutes > 0) {
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  } else {
    return `${seconds}s`
  }
}

/**
 * Formats a duration between two dates to a human-readable string
 * @param startDate - The start date (can be string or Date)
 * @param endDate - The end date (can be string or Date)
 * @returns A formatted string like "2h 30m", "45m 10s", or "30s"
 */
export const formatDurationBetweenDates = (
  startDate: string | Date,
  endDate: string | Date
): string => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate
  const duration = end.getTime() - start.getTime()
  return formatDuration(duration)
}

export const commaSeparatedListWithAnd = (list: string[]) => {
  if (list.length === 0) return ''
  if (list.length === 1) return list[0]
  if (list.length === 2) return `${list[0]}, and ${list[1]}`
  else {
    const lastItem = list.pop()
    return `${list.join(', ')}, and ${lastItem}`
  }
}
